/**
 * iter182 — Pay Now (UPI Deep Link) Modal.
 *
 * Renders a Kahani-luxury modal for a single milestone payment.
 *
 * • Builds a spec-compliant `upi://pay?...` intent URI with the studio
 *   UPI ID, payee name, amount and a couple/milestone transaction note.
 * • Primary CTA "PAY WITH UPI" — attempts to hand off to any installed
 *   UPI app (GPay, PhonePe, Paytm, BHIM, etc.) via `window.location`.
 *   Never opens WhatsApp.
 * • Always shows UPI ID + Copy + QR as reliable fallbacks, because
 *   some UPI apps cap QR-scan payments at ₹2,000 — for larger amounts
 *   the copy-UPI-ID path is the client's guaranteed way to pay the
 *   full milestone amount.
 * • After the user acts (or clicks "I've already paid"), we prompt a
 *   friendly confirmation. "Yes" bubbles up via `onPaidConfirmed(m)`
 *   so the parent can scroll to the PaymentProofUploader + preselect
 *   the milestone.
 *
 * This component never mutates the ledger — payment recording remains
 * exclusively with Admin verification of the uploaded screenshot.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { toast } from "sonner";
import { inr } from "@/lib/utils";
import { Copy, Check, X, Smartphone, Wallet, ScanLine, Info } from "lucide-react";

function buildUpiUri({ upiId, payeeName, amount, note }) {
  const params = new URLSearchParams();
  params.set("pa", upiId);
  if (payeeName) params.set("pn", payeeName);
  if (amount && amount > 0) params.set("am", Number(amount).toFixed(2));
  params.set("cu", "INR");
  if (note) params.set("tn", String(note).slice(0, 80));
  return `upi://pay?${params.toString()}`;
}

// UPI QR chat-transfer limit (₹2,000) beyond which some apps refuse
// a QR-based transfer. We surface a UPI-ID hint above that threshold.
const QR_HINT_THRESHOLD = 2000;

export default function PayNowModal({
  open,
  onClose,
  milestone,
  details,
  coupleName,
  onPaidConfirmed,
}) {
  const [copied, setCopied] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const confirmTimerRef = useRef(null);

  useEffect(() => {
    if (!open) {
      setConfirming(false);
      setCopied(false);
      if (confirmTimerRef.current) {
        clearTimeout(confirmTimerRef.current);
        confirmTimerRef.current = null;
      }
    }
  }, [open]);

  useEffect(() => () => {
    if (confirmTimerRef.current) clearTimeout(confirmTimerRef.current);
  }, []);

  const payeeName =
    details?.upi_display_name ||
    details?.account_holder_name ||
    "Kahani Studio";

  // Prefer the unpaid remainder; fall back to full amount if paid_amount
  // is not present (backend historically only ships this on partials).
  const paidAmt = Number(milestone?.paid_amount || 0);
  const grossAmt = Number(milestone?.amount || 0);
  const remaining = Math.max(0, grossAmt - paidAmt);
  const displayAmt = remaining > 0 ? remaining : grossAmt;

  const noteLabel = milestone?.label || milestone?.milestone_type || "Payment";
  const note = coupleName ? `${coupleName} - ${noteLabel}` : noteLabel;

  const upiUri = useMemo(() => {
    if (!details?.upi_id) return "";
    return buildUpiUri({
      upiId: details.upi_id,
      payeeName,
      amount: displayAmt,
      note,
    });
  }, [details?.upi_id, payeeName, displayAmt, note]);

  if (!open || !milestone) return null;

  const launchUpi = () => {
    if (!upiUri) return;
    // eslint-disable-next-line no-restricted-globals
    window.location.href = upiUri;
    // After the intent handoff, prompt confirmation shortly. Timer is
    // cleared on unmount / close (see effects above) so it never
    // resurrects a closed modal.
    if (confirmTimerRef.current) clearTimeout(confirmTimerRef.current);
    confirmTimerRef.current = setTimeout(() => {
      setConfirming(true);
      confirmTimerRef.current = null;
    }, 3500);
  };

  const copyUpi = async () => {
    try {
      await navigator.clipboard.writeText(details.upi_id);
      setCopied(true);
      toast.success("UPI ID copied");
      setTimeout(() => setCopied(false), 1600);
    } catch {
      toast.error("Copy failed");
    }
  };

  const handleClose = () => {
    setConfirming(false);
    onClose?.();
  };

  const handleYes = () => {
    onPaidConfirmed?.(milestone);
    handleClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={handleClose}
      data-testid="pay-now-modal"
      role="dialog"
      aria-modal="true"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-[#FDF7EF] rounded-lg border border-[#E7D5B4] max-w-md w-full max-h-[92vh] overflow-y-auto shadow-2xl"
      >
        {/* Header */}
        <div className="p-6 pb-4 border-b border-[#E7D5B4]/70 flex items-start gap-3">
          <div className="w-11 h-11 rounded-full bg-[#F5EBDC] border border-[#E7D5B4] flex items-center justify-center shrink-0">
            <Wallet className="w-5 h-5 text-[#9C7550]" strokeWidth={1.8} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="editorial-eyebrow text-[#9C7550]">Pay Now · UPI</div>
            <div
              className="font-heading text-lg text-[#3B2F24] truncate"
              data-testid="pay-now-milestone-label"
            >
              {noteLabel}
            </div>
            <div
              className="font-heading text-3xl text-[#3B2F24] mt-1 tabular-nums"
              data-testid="pay-now-amount"
            >
              {inr(displayAmt)}
            </div>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="text-[#9C7550] hover:text-[#3B2F24] p-1"
            aria-label="Close"
            data-testid="pay-now-close-btn"
          >
            <X className="w-5 h-5" strokeWidth={1.8} />
          </button>
        </div>

        {/* Body */}
        {confirming ? (
          <div className="p-6 space-y-4" data-testid="pay-now-confirm-view">
            <div className="font-heading text-xl text-[#3B2F24]">
              Did you complete the payment?
            </div>
            <p className="text-sm text-[#6E5B49] leading-relaxed">
              If yes, we'll take you to upload a screenshot as proof so
              our team can verify and mark it received.
            </p>
            <div className="flex flex-col sm:flex-row gap-2 pt-2">
              <button
                type="button"
                onClick={handleYes}
                className="flex-1 px-4 py-3 rounded-full bg-[#3B2F24] text-white text-xs font-semibold uppercase tracking-widest hover:bg-[#231b13] transition-colors"
                data-testid="pay-now-yes-btn"
              >
                Yes · Upload Proof
              </button>
              <button
                type="button"
                onClick={handleClose}
                className="flex-1 px-4 py-3 rounded-full border border-[#9C7550] text-[#9C7550] text-xs font-semibold uppercase tracking-widest hover:bg-[#F5EBDC] transition-colors"
                data-testid="pay-now-no-btn"
              >
                Not Yet
              </button>
            </div>
          </div>
        ) : (
          <div className="p-6 space-y-5">
            {!details?.upi_id ? (
              <div
                className="text-sm text-[#B04A32] bg-[#F5DBD3] border border-[#DBB8AA] rounded-md p-3"
                data-testid="pay-now-no-upi"
              >
                Studio UPI ID isn't configured yet. Please contact the
                studio for payment instructions.
              </div>
            ) : (
              <>
                {/* Primary action — always visible. Attempts to hand off
                     to any installed UPI app (GPay / PhonePe / Paytm /
                     BHIM / …). Never opens WhatsApp. */}
                <button
                  type="button"
                  onClick={launchUpi}
                  className="w-full inline-flex items-center justify-center gap-2 px-4 py-4 rounded-full bg-[#3B2F24] text-white text-sm font-semibold uppercase tracking-widest hover:bg-[#231b13] transition-colors"
                  data-testid="pay-now-launch-upi-btn"
                >
                  <Smartphone className="w-4 h-4" strokeWidth={2} />
                  Pay With UPI
                </button>

                {/* UPI ID + Copy — the reliable fallback that always
                     works, even when a QR scan is blocked / capped. */}
                <div className="flex items-center gap-2 justify-between border border-[#E7D5B4] rounded-md px-3 py-2 bg-white">
                  <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-widest text-[#9C7550] font-semibold">
                      UPI ID
                    </div>
                    <div className="text-sm font-mono text-[#3B2F24] truncate">
                      {details.upi_id}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={copyUpi}
                    className="px-3 py-1.5 rounded-full border border-[#E7D5B4] text-[10px] uppercase tracking-widest font-semibold text-[#9C7550] hover:bg-[#F5EBDC] transition-colors inline-flex items-center gap-1"
                    data-testid="pay-now-upi-copy-btn"
                  >
                    {copied ? (
                      <>
                        <Check className="w-3 h-3" strokeWidth={2.4} /> Copied
                      </>
                    ) : (
                      <>
                        <Copy className="w-3 h-3" strokeWidth={2} /> Copy UPI ID
                      </>
                    )}
                  </button>
                </div>

                {/* Large-amount fallback note. Some UPI apps cap
                     QR-scan payments at ₹2,000; the UPI-ID path bypasses
                     that limit, so we make it explicit for big
                     milestones like Booking Amount / Final Payment. */}
                {displayAmt >= QR_HINT_THRESHOLD && (
                  <div
                    className="flex items-start gap-2 text-[11px] text-[#7A5A2E] bg-[#F5EBDC]/60 border border-[#E7D5B4] rounded-md px-3 py-2 leading-relaxed"
                    data-testid="pay-now-large-amount-note"
                  >
                    <Info className="w-3.5 h-3.5 mt-0.5 shrink-0 text-[#9C7550]" strokeWidth={2} />
                    <span>
                      If QR payment is limited by your UPI app, copy the
                      UPI ID above and pay the full amount ({inr(displayAmt)})
                      directly from your UPI app.
                    </span>
                  </div>
                )}

                {/* QR fallback — encodes the same UPI intent as the
                     primary button, so a phone camera scan launches
                     with the amount + note pre-filled. */}
                <div className="pt-2">
                  <div className="text-[10px] uppercase tracking-widest text-[#9C7550] font-semibold text-center mb-2 inline-flex items-center gap-1.5 justify-center w-full">
                    <ScanLine className="w-3 h-3" strokeWidth={2} /> Or scan with any UPI app
                  </div>
                  <div className="flex flex-col items-center gap-2">
                    <div
                      className="p-4 bg-white border border-[#E7D5B4] rounded-md"
                      data-testid="pay-now-qr-wrap"
                    >
                      <QRCodeCanvas
                        value={upiUri}
                        size={200}
                        level="M"
                        bgColor="#FFFFFF"
                        fgColor="#3B2F24"
                        includeMargin={false}
                      />
                    </div>
                    <div className="text-[10px] uppercase tracking-widest text-[#B18860] text-center">
                      Payee · {payeeName}
                    </div>
                  </div>
                </div>

                <div className="pt-2 border-t border-[#E7D5B4]/60 text-center">
                  <button
                    type="button"
                    onClick={() => setConfirming(true)}
                    className="text-xs text-[#9C7550] hover:text-[#3B2F24] underline decoration-dotted underline-offset-4 font-semibold uppercase tracking-widest"
                    data-testid="pay-now-already-paid-btn"
                  >
                    I've already paid — upload screenshot
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
